import React, { FunctionComponent, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Label, Tabs, Typography } from '@inplayer-org/inplayer-ui';
import { Field, useField, useFormikContext } from 'formik';
import { Moment } from 'moment';
import styled from 'styled-components';
import { MdInfoOutline } from 'react-icons/md';

// actions
import { loadCodeSessions, terminateCodeSession } from 'ducks/merchantDucks/items/actions';
import { openModal } from 'ducks/merchantDucks/modal/actions';

// constants
import Rules from 'restrictions/roles/constants/rules';

// enums
import { ButtonTypes } from 'pages/MerchantPages/Assets/Codes/containers/CodesContainer';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// selectors
import { getCodeSessions } from 'ducks/merchantDucks/items/selectors';

// types
import { CodeType } from 'ducks/merchantDucks/items/types';
import { ModalNames } from 'ducks/merchantDucks/modal/types';

// components
import StyledContainer from 'components/StyledContainer';
import Input from 'components/Fields/Input';
import FieldError from 'components/Fields/FieldError';
import SingleDayPicker from 'components/SingleDayPicker';
import TimeHourPicker from 'components/TimeHourPicker';
import Select from 'components/Fields/Select';
import SessionTable from 'pages/MerchantPages/Assets/Codes/components/SessionsTable';
import { CustomButton } from 'pages/MerchantPages/Assets/Price/containers/PriceOptions';
import ConfirmModal from 'components/Modals/ConfirmModal';
import { TooltipCell, TooltipStyled } from '../../Vouchers/shared/sharedComponents';

const { Cell, Container } = Grid;

const FlexCell = styled(Cell)`
  display: flex;
  flex-direction: column;
`;

const StyledLabel = styled(Label)`
  margin-bottom: 8px;
`;

export enum CodeTabs {
  CodeDetails = 0,
  CodeSessions = 1,
}

export enum CodeTypes {
  PRIVATE = 'private',
  PUBLIC = 'public',
}

interface DateTimeFieldProps {
  name: string;
  dateCellWidth?: number;
  timeCellWidth?: number;
}

interface Props {
  codeDetails: CodeType;
  buttonType: string;
  activeTab: number;
  handleBackToCodesTable: () => void;
  setActiveTab: (tabIndex: number) => void;
  resetForm: () => any;
}

const codeTabs: Array<{ name: string }> = [{ name: 'Details' }, { name: 'Sessions' }];

const AssetCodeOptions: FunctionComponent<Props> = ({
  codeDetails,
  buttonType,
  activeTab,
  handleBackToCodesTable,
  setActiveTab,
  resetForm,
}) => {
  const dispatch = useDispatch();
  const { validateField, setFieldValue, setFieldTouched } = useFormikContext();
  const [browserFingerprint, setBrowserFingerprint] = useState('');
  const [selectedCodeType, setSelectedCodeType] = useState('');

  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);

  const codeTypes = [
    { displayName: CodeTypes.PUBLIC, value: CodeTypes.PUBLIC },
    { displayName: CodeTypes.PRIVATE, value: CodeTypes.PRIVATE },
  ];

  const sessions: any = useSelector(getCodeSessions);

  useEffect(() => {
    setSelectedCodeType(codeDetails.type);

    if (codeDetails.type === CodeTypes.PRIVATE && activeTab === CodeTabs.CodeSessions) {
      dispatch(loadCodeSessions(codeDetails.id));
    }
  }, [activeTab, codeDetails.id, codeDetails.type, dispatch]);

  const handleShowModal = (selectedBrowserFingerprint: string) => {
    setBrowserFingerprint(selectedBrowserFingerprint);
    dispatch(openModal(ModalNames.TerminateSession));
  };

  const handleTerminateSession = () => {
    dispatch(terminateCodeSession(codeDetails.id, browserFingerprint));
  };

  const handleChangeActiveTab = (i: number) => setActiveTab(i);

  const handleSelectCodeType = (type: string) => {
    setSelectedCodeType(type);
    if (codeDetails.id && type === CodeTypes.PUBLIC) {
      // Reset concurrentSessions value when private code type is changed to public
      if (buttonType === ButtonTypes.UPDATE) {
        setFieldValue('concurrentSessions', '');
        return;
      }
      resetForm();
    }
  };

  const handleDateTimeChange = (fieldName: string) => async (datetime: Moment) => {
    await setFieldValue(fieldName, datetime);
    await setFieldTouched(fieldName, true);
    validateField(fieldName);
  };

  const DateTimeField = (props: DateTimeFieldProps) => {
    const [field] = useField(props);
    const { value: fieldValue } = field;
    const { name: fieldName, dateCellWidth, timeCellWidth } = props;

    return (
      <>
        <Cell width={dateCellWidth || 6} middle>
          <SingleDayPicker date={fieldValue} handleDateChange={handleDateTimeChange(fieldName)} disabled={isReadOnly} />
        </Cell>
        <Cell width={timeCellWidth || 4} middle>
          <TimeHourPicker
            handleTimeChange={handleDateTimeChange(fieldName)}
            time={fieldValue}
            placeholder="Time"
            disabled={isReadOnly}
          />
        </Cell>
      </>
    );
  };

  const renderCodeContent = () => {
    const showCodeField = buttonType === ButtonTypes.ADD || buttonType === ButtonTypes.UPDATE;
    const disableConcurrentSessions = isReadOnly || selectedCodeType === CodeTypes.PUBLIC;

    return activeTab === CodeTabs.CodeDetails ? (
      <>
        <StyledContainer rowGap="1rem" columns={12}>
          {showCodeField && (
            <Cell width={12}>
              <Field
                label="Passcode"
                name="code"
                type="text"
                component={Input}
                placeholder="Passcode"
                disabled={buttonType === ButtonTypes.UPDATE}
              />
              <FieldError name="code" />
            </Cell>
          )}
          <FlexCell width={6}>
            <StyledLabel>Validity start date & time (UTC)</StyledLabel>
            <Container columns={12}>
              <DateTimeField name="startDate" />
              <FieldError name="startDate" />
            </Container>
          </FlexCell>
          <FlexCell width={6}>
            <StyledLabel>Validity end date & time (UTC)</StyledLabel>
            <Container columns={6}>
              <DateTimeField name="endDate" dateCellWidth={4} timeCellWidth={2} />
              <FieldError name="endDate" />
            </Container>
          </FlexCell>
          <Cell width={6}>
            <Container columns={12}>
              <TooltipCell width={12}>
                <Label>Passcode type</Label>
                <TooltipStyled
                  placement="right"
                  content={`Choose a private or public passcode type. \n Public passcodes can be used by an unlimited number of customers simultaneously. \n Private passcodes can have a limit on the number of simultaneous uses.`}
                >
                  <MdInfoOutline />
                </TooltipStyled>
              </TooltipCell>
              <Cell width={12}>
                <Field
                  name="type"
                  component={Select}
                  defaultOption={{ displayName: 'Select type' }}
                  options={codeTypes}
                  value={selectedCodeType}
                  handleChange={handleSelectCodeType}
                  disabled={isReadOnly}
                />
                <FieldError name="type" />
              </Cell>
            </Container>
          </Cell>
          <Cell width={7}>
            <Container columns={12}>
              <TooltipCell width={12}>
                <Label>Simultaneous uses</Label>
                <TooltipStyled
                  placement="right"
                  content={` Limit the number of customers that can use this passcode at the same time. \n The default is unlimited. This setting is only available for private-type passcodes.`}
                >
                  <MdInfoOutline />
                </TooltipStyled>
              </TooltipCell>
              <Cell width={12}>
                <Field
                  name="concurrentSessions"
                  type="text"
                  component={Input}
                  placeholder="Simultaneous uses"
                  disabled={disableConcurrentSessions}
                />
                <FieldError name="concurrentSessions" />
              </Cell>
            </Container>
          </Cell>
        </StyledContainer>
      </>
    ) : (
      <>
        <Container columns={12} rowGap="1rem">
          <Cell width={12}>
            <Typography variant="p">Active Sessions</Typography>
            <SessionTable sessions={sessions} handleShowModal={handleShowModal} />
          </Cell>
        </Container>
        <ConfirmModal
          modalName={ModalNames.TerminateSession}
          handleConfirm={handleTerminateSession}
          message="Are you sure you want to terminate the session?"
        />
      </>
    );
  };

  return (
    <Container columns={4} rowGap="2rem">
      <Cell middle>
        <CustomButton buttonModifiers={['buttonLink']} type="button" onClick={handleBackToCodesTable}>
          &larr; back
        </CustomButton>
      </Cell>
      {buttonType === ButtonTypes.UPDATE && codeDetails.type === CodeTypes.PRIVATE && (
        <Cell width={2}>
          <Tabs tabs={codeTabs} selectedTabIndex={activeTab} onTabClick={handleChangeActiveTab} />
        </Cell>
      )}
      <Cell width={4}>{renderCodeContent()}</Cell>
    </Container>
  );
};

export default AssetCodeOptions;
